import { focusManager } from "@tanstack/react-query"
import { Modal } from "antd"
import { lazy, memo, Suspense, useCallback, useEffect, useRef } from "react"

import { submitNPSSurvey, updateUserFeatureFlags } from "~/api/user"
import { useCheckEmailConnection } from "~/hooks/user"
import useGlobalStore from "~/store/global"
import useOsLiteStore from "~/store/os-lite"

const InitalizeFormBricks = lazy(
  () => import("~/containers/initalize-formbricks")
)
const ConnectEmailProviders = lazy(
  () => import("~/components/connect-email-provider")
)
const WhatsNewModal = lazy(() => import("./whats-new"))

const OsLiteAddAccounts = lazy(() => import("./os-lite-add-accounts"))

function RootQueries() {
  const connectEmailProviderModalOpen = useGlobalStore(
    (store) => store.connectEmailProviderModalOpen
  )
  const featureFlags = useGlobalStore((store) => store.featureFlags)
  const addAccounts = useOsLiteStore((store) => store.addAccounts)

  const surveySubmittedRef = useRef(false)
  const [modalApi, modalContextHolder] = Modal.useModal()
  useCheckEmailConnection()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  useEffect(() => {
    if (window.top && window.top !== window.self) {
      window.top.location.href = window.location.href
    }
  }, [])

  const handleMessageEvents = useCallback(
    async (event: MessageEvent) => {
      if (
        event.data === "formbricksSurveyCompleted" &&
        !surveySubmittedRef.current &&
        tokenInfo
      ) {
        try {
          await submitNPSSurvey(tokenInfo)
          await updateUserFeatureFlags(tokenInfo, {
            nps_survey_completed: true
          })
          surveySubmittedRef.current = true
          import("@formbricks/js").then(({ default: formbricks }) => {
            formbricks.logout()
          })
          localStorage.removeItem("formbricks-js")
        } catch {
          // do nothing
        }
      }
    },
    [tokenInfo]
  )

  useEffect(() => {
    let offlineModal: any
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        focusManager.setFocused(true)
      }
    }
    window.addEventListener("online", () => {
      offlineModal?.destroy()
    })
    window.addEventListener("offline", () => {
      offlineModal = modalApi.error({
        title: "You're Offline",
        content: (
          <p className="text-13">
            It looks like you&apos;re not connected to the internet. Please
            check your connection and try again.
          </p>
        ),
        centered: true,
        closable: false,
        footer: null,
        classNames: {
          content: "!p-5"
        }
      })
    })
    window.addEventListener("visibilitychange", handleVisibilityChange)

    window.addEventListener("message", handleMessageEvents)
    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("message", handleMessageEvents, false)
      surveySubmittedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Suspense fallback={null}>
      {modalContextHolder}
      {connectEmailProviderModalOpen && <ConnectEmailProviders />}
      <WhatsNewModal />
      {addAccounts && <OsLiteAddAccounts />}
      {featureFlags?.nps_survey?.survey_id && <InitalizeFormBricks />}
    </Suspense>
  )
}

export default memo(RootQueries)
